import React, { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from '../../shared/contexts/WebSocketContext';
import AdminNavBar from './AdminNavBar';
import {
  adminErrorsService,
  ErrorCategory,
  ErrorLogEntry,
  ErrorsFilter,
  ErrorSeverity
} from '../../services/admin/admin-errors.service';
import './AdminErrorsPage.css';

// ── Helpers ────────────────────────────────────────────────────────────────

const SEVERITY_ORDER: ErrorSeverity[] = ['critical', 'high', 'medium', 'low'];
const CATEGORIES: ErrorCategory[] = [
  'auth',
  'validation',
  'database',
  'external_api',
  'ingestion',
  'ai',
  'unknown'
];

function severityBadgeClass(s: ErrorSeverity) {
  return (
    { critical: 'danger', high: 'warning', medium: 'info', low: 'secondary' }[
      s
    ] ?? 'secondary'
  );
}

// ── Stack trace drawer ─────────────────────────────────────────────────────

function DetailDrawer({
  entry,
  onClose
}: {
  entry: ErrorLogEntry;
  onClose: () => void;
}) {
  return (
    <div
      className='aerr-drawer-backdrop'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-label='Error detail'
    >
      <div
        className='aerr-drawer'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='aerr-drawer-header'>
          <h5 className='aerr-drawer-title'>{entry.message}</h5>
          <button
            className='btn-close'
            aria-label='Close'
            onClick={onClose}
          />
        </div>
        <dl className='aerr-drawer-meta'>
          <dt>Severity</dt>
          <dd>
            <span className={`badge bg-${severityBadgeClass(entry.severity)}`}>
              {entry.severity}
            </span>
          </dd>
          <dt>Category</dt>
          <dd>{entry.category}</dd>
          <dt>Count</dt>
          <dd>{entry.count.toLocaleString()}</dd>
          <dt>Last occurrence</dt>
          <dd>{new Date(entry.lastOccurrence).toLocaleString()}</dd>
          {entry.acknowledgedAt && (
            <>
              <dt>Acknowledged</dt>
              <dd>{new Date(entry.acknowledgedAt).toLocaleString()}</dd>
            </>
          )}
          {entry.context?.endpoint && (
            <>
              <dt>Endpoint</dt>
              <dd className='aerr-mono'>{entry.context.endpoint}</dd>
            </>
          )}
        </dl>
        {entry.context?.stack && (
          <div className='aerr-stack-section'>
            <div className='aerr-stack-label'>Stack trace</div>
            <pre className='aerr-stack'>{entry.context.stack}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AdminErrorsPage() {
  const [items, setItems] = useState<ErrorLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<ErrorsFilter>({});
  const [selected, setSelected] = useState<ErrorLogEntry | null>(null);
  const [liveCount, setLiveCount] = useState(0);
  const { socket } = useWebSocket();

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setError('');
      try {
        const result = await adminErrorsService.list({ ...filter, page: p });
        setItems(result.items);
        setTotal(result.total);
        setPages(result.pages);
        setPage(p);
      } catch {
        setError('Failed to load error logs');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, page]
  );

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Increment live counter on incoming admin:error events
  useEffect(() => {
    if (!socket) return;
    const handler = () => setLiveCount((n) => n + 1);
    socket.on('admin:error', handler);
    return () => {
      socket.off('admin:error', handler);
    };
  }, [socket]);

  const handleAcknowledge = async (id: string) => {
    try {
      const updated = await adminErrorsService.acknowledge(id);
      setItems((prev) => prev.map((e) => (e._id === id ? updated : e)));
      if (selected?._id === id) setSelected(updated);
    } catch {
      // Silently ignore; next refresh will reflect the server state
    }
  };

  const applyFilter = (patch: Partial<ErrorsFilter>) =>
    setFilter((f) => ({ ...f, ...patch }));

  const clearFilters = () => setFilter({});

  return (
    <div className='container-fluid aerr-page'>
      <AdminNavBar />

      <div className='aerr-toolbar'>
        <h4 className='aerr-title'>
          Error Logs
          {total > 0 && (
            <span className='aerr-total ms-2 text-muted'>
              ({total.toLocaleString()} total)
            </span>
          )}
        </h4>
        {liveCount > 0 && (
          <span className='badge bg-danger aerr-live-badge'>
            +{liveCount} new
          </span>
        )}
        <button
          className='btn btn-sm btn-outline-secondary ms-auto'
          onClick={() => {
            setLiveCount(0);
            void load(1);
          }}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────── */}
      <div className='aerr-filters'>
        <select
          className='form-select form-select-sm aerr-filter-select'
          value={filter.severity ?? ''}
          onChange={(e) =>
            applyFilter({
              severity: (e.target.value as ErrorSeverity) || undefined
            })
          }
          aria-label='Filter by severity'
        >
          <option value=''>All severities</option>
          {SEVERITY_ORDER.map((s) => (
            <option
              key={s}
              value={s}
            >
              {s}
            </option>
          ))}
        </select>

        <select
          className='form-select form-select-sm aerr-filter-select'
          value={filter.category ?? ''}
          onChange={(e) =>
            applyFilter({
              category: (e.target.value as ErrorCategory) || undefined
            })
          }
          aria-label='Filter by category'
        >
          <option value=''>All categories</option>
          {CATEGORIES.map((c) => (
            <option
              key={c}
              value={c}
            >
              {c}
            </option>
          ))}
        </select>

        <select
          className='form-select form-select-sm aerr-filter-select'
          value={
            filter.acknowledged === undefined ? '' : String(filter.acknowledged)
          }
          onChange={(e) =>
            applyFilter({
              acknowledged:
                e.target.value === '' ? undefined : e.target.value === 'true'
            })
          }
          aria-label='Filter by acknowledgement'
        >
          <option value=''>All</option>
          <option value='false'>Unacknowledged</option>
          <option value='true'>Acknowledged</option>
        </select>

        {Object.keys(filter).length > 0 && (
          <button
            className='btn btn-sm btn-link aerr-clear-btn'
            onClick={clearFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && <div className='alert alert-danger m-3'>{error}</div>}

      {/* ── Table ────────────────────────────────────────────────────── */}
      <div className='table-responsive aerr-table-wrapper'>
        <table className='table table-sm table-hover aerr-table'>
          <thead>
            <tr>
              <th>Severity</th>
              <th>Category</th>
              <th>Message</th>
              <th className='text-end'>Count</th>
              <th>Last Occurrence</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={7}
                  className='text-center py-4'
                >
                  <div
                    className='spinner-border spinner-border-sm text-primary'
                    role='status'
                  />
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className='text-center text-muted py-4'
                >
                  No errors found.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((e) => (
                <tr
                  key={e._id}
                  className={e.acknowledgedAt ? 'aerr-row--acked' : ''}
                >
                  <td>
                    <span
                      className={`badge bg-${severityBadgeClass(e.severity)}`}
                    >
                      {e.severity}
                    </span>
                  </td>
                  <td className='aerr-category'>{e.category}</td>
                  <td
                    className='aerr-message'
                    title={e.message}
                  >
                    {e.message}
                  </td>
                  <td className='text-end'>{e.count.toLocaleString()}</td>
                  <td className='aerr-date'>
                    {new Date(e.lastOccurrence).toLocaleString()}
                  </td>
                  <td>
                    {e.acknowledgedAt ? (
                      <span className='badge bg-secondary'>Acked</span>
                    ) : (
                      <span className='badge bg-warning text-dark'>Open</span>
                    )}
                  </td>
                  <td className='aerr-actions'>
                    <button
                      className='btn btn-xs btn-outline-secondary aerr-detail-btn'
                      onClick={() => setSelected(e)}
                    >
                      Detail
                    </button>
                    {!e.acknowledgedAt && (
                      <button
                        className='btn btn-xs btn-outline-success aerr-ack-btn'
                        onClick={() => void handleAcknowledge(e._id)}
                      >
                        Ack
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ───────────────────────────────────────────────── */}
      {pages > 1 && (
        <nav
          className='aerr-pagination'
          aria-label='Error log pagination'
        >
          <button
            className='btn btn-sm btn-outline-secondary'
            disabled={page <= 1}
            onClick={() => void load(page - 1)}
          >
            ‹ Prev
          </button>
          <span className='aerr-page-info'>
            Page {page} of {pages}
          </span>
          <button
            className='btn btn-sm btn-outline-secondary'
            disabled={page >= pages}
            onClick={() => void load(page + 1)}
          >
            Next ›
          </button>
        </nav>
      )}

      {/* ── Detail drawer ────────────────────────────────────────────── */}
      {selected && (
        <DetailDrawer
          entry={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
